import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import MailOutlineIcon from "@mui/icons-material/MailOutline"; // MUI icon
import CloseIcon from "@mui/icons-material/Close";
import MDBox from "components/MDBox";
import { Avatar, Card, Paper } from "@mui/material";
import OtpInputForm from "../OtpSignInModal/OtpInputForm";
import { OTP_LENGTH } from "constants/authConstants";
import { useState } from "react";
import MDTypography from "components/MDTypography";
import { useOtp } from "context/OtpContext";
import PropTypes from "prop-types";
import KnowMeForm from "./KnowMeForm";
import SimpleBackdrop from "components/MDLoader_Backdrop";
import PhoneIcon from "@mui/icons-material/Phone";
import LoginProcessAuth from "../LoginProcessAuth";
import { OTP_LOGIN_ACTION } from "constants/authConstants";

const isPhoneNumber = (value) => {
  const digitsOnly = value?.replace(/\D/g, "");
  return /^(\+?[0-9]{10,15})$/.test(digitsOnly);
};

export default function AuthModal({
  authSection,
  open,
  setOpen,
  campaignCode,
  referrerCode,
  storeId,
  brandId,
  logoStatus,
  logoIdUrl,
  CateringReward,
  gtmCode,
  campaignDetails,
}) {
  const [level, setLevel] = useState(0);
  const { isOtpSent, isLoading, message, identifier, dispatch } = useOtp();

  const handleClose = () => {
    setOpen(false);
    setLevel(0);
    dispatch({ type: OTP_LOGIN_ACTION.BACK });
  };

  return (
    <>
      <Dialog fullScreen open={open} onClose={handleClose}>
        <MDBox
          display="flex"
          flexDirection="column"
          sx={{
            alignItems: "center",
            justifyContent: "center",
            background:
              "linear-gradient(135deg, rgba(26, 233, 98, 0.08) 0%, rgba(255, 255, 255, 1) 100%)",
            position: "relative",
            height: "100vh",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(26, 233, 98, 0.3)",
              borderRadius: "3px",
            },
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
              radial-gradient(circle at 10% 10%, rgba(26, 233, 98, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 90% 90%, rgba(26, 233, 98, 0.1) 0%, transparent 40%),
              radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.9) 0%, transparent 70%)
            `,
              pointerEvents: "none",
            },
          }}
        >
          {/* Close icon */}
          <IconButton
            sx={{
              position: "absolute",
              top: 20,
              right: 50,
              bgcolor: "rgba(0, 0, 0, 0.2)",
              padding: 1,
              borderRadius: "8px",
              "&:hover": {
                bgcolor: "rgba(255, 255, 255, 1)",
              },
            }}
            edge="start"
            color="secondary"
            onClick={handleClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <>
            {/* Logo */}
            <Paper
              elevation={3}
              sx={{
                p: 1.5,
                mb: 2.5,
                borderRadius: "16px",
                background: "#fff",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "110px",
                height: "110px",
                boxShadow: "0 10px 20px rgba(0, 0, 0, 0.08)",
                position: "relative",
                zIndex: isLoading ? 0 : 999999,
              }}
            >
              <Avatar
                src={
                  logoStatus === 2 ? "" : logoStatus === 1 && logoIdUrl ? logoIdUrl : CateringReward
                }
                sx={{
                  borderRadius: "0px",
                  width: "100px",
                  height: "auto",
                }}
              />
            </Paper>

            <MDTypography
              variant="h1"
              sx={{
                fontSize: { xs: "1.85rem", md: "2rem" },
                fontWeight: 800,
                color: "#1A202C",
                position: "relative",
                zIndex: isLoading ? 0 : 999999,
                mb: 1,
                textTransform: "none",
                letterSpacing: "-0.02em",
              }}
            >
              Welcome!
            </MDTypography>

            {authSection === "password" && (
              <LoginProcessAuth
                gtmCode={gtmCode}
                campaignCode={campaignCode}
                referrerCode={referrerCode}
                storeId={storeId}
                brandId={brandId}
                campaignDetails={campaignDetails}
              />
            )}

            {authSection === "code" && (
              <>
                {level === 0 ? (
                  <Card sx={{ margin: 2, marginLeft: 3, maxWidth: "548px" }}>
                    <MDBox mx={2} p={2} textAlign="center" minWidth={{ xs: "320px", xl: "380px" }}>
                      {/*Show Email/Phone Icon Icon On OTP Sent */}
                      {isOtpSent && (
                        <MDBox
                          mb={"24px"}
                          mt={"12px"}
                          display="flex"
                          justifyContent="center"
                          alignItems="center"
                        >
                          <Avatar
                            sx={{
                              bgcolor: "#CEFFE1",
                              width: 48,
                              height: 48,
                            }}
                          >
                            {isPhoneNumber(identifier) ? (
                              <PhoneIcon sx={{ fontSize: 24, color: "rgb(16, 121, 72)" }} />
                            ) : (
                              <MailOutlineIcon sx={{ fontSize: 24, color: "rgb(16, 121, 72)" }} />
                            )}{" "}
                          </Avatar>
                        </MDBox>
                      )}

                      <MDTypography
                        sx={{ color: "#101828", fontSize: "28px" }}
                        variant="h4"
                        fontWeight="medium"
                        mt={1}
                        mb={"20px"}
                      >
                        {!isOtpSent
                          ? "Log in or sign up"
                          : `Please check your ${isPhoneNumber(identifier) ? "SMS" : "email"} `}
                      </MDTypography>
                    </MDBox>

                    <MDBox pb={"24px"} px={"24px"}>
                      <OtpInputForm
                        handleBack={() => handleClose()}
                        setLevel={setLevel}
                        length={OTP_LENGTH}
                        gtmCode={gtmCode}
                        campaignCode={campaignCode}
                        campaignDetails={campaignDetails}
                        storeId={storeId}
                        brandId={brandId}
                      />
                    </MDBox>
                  </Card>
                ) : (
                  <Card sx={{ margin: 2, marginLeft: 3, maxWidth: "408px" }}>
                    <MDBox mx={2} p={2} textAlign="center" minWidth={{ xs: "320px", xl: "380px" }}>
                      <MDTypography
                        sx={{ color: "#101828", fontSize: "28px" }}
                        variant="h4"
                        fontWeight="medium"
                        mt={1}
                        mb={"20px"}
                      >
                        Let us get to know you
                      </MDTypography>
                    </MDBox>

                    <MDBox pb={"24px"} px={"24px"}>
                      <KnowMeForm
                        setLevel={setLevel}
                        campaignCode={campaignCode}
                        referrerCode={referrerCode}
                        storeId={storeId}
                        brandId={brandId}
                        gtmCode={gtmCode}
                        campaignDetails={campaignDetails}
                      />
                    </MDBox>

                    <MDTypography
                      mx={2}
                      px={2}
                      pb={2}
                      variant="body2"
                      textAlign={"center"}
                      style={{
                        maxWidth: "360px",
                        fontSize: "0.8rem",
                        color: message && message.type === "error" ? "#f44336" : "#238856",
                        fontWeight: 500,
                      }}
                    >
                      {message?.type === "error" && message?.message}
                    </MDTypography>
                  </Card>
                )}
              </>
            )}

            {isLoading && <SimpleBackdrop />}
          </>
        </MDBox>
      </Dialog>
    </>
  );
}

AuthModal.propTypes = {
  authSection: PropTypes.any,
  setOpen: PropTypes.func,
  open: PropTypes.bool,
  logoStatus: PropTypes.number.isRequired,
  logoIdUrl: PropTypes.string,
  CateringReward: PropTypes.string,
  campaignCode: PropTypes.string,
  referrerCode: PropTypes.string,
  storeId: PropTypes.string,
  brandId: PropTypes.string,
  gtmCode: PropTypes.string,
  campaignDetails: PropTypes.object,
};
